import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { PasswordStrength, allCriteriaMet } from '@/components/PasswordStrength';

const monoFont = "'IBM Plex Mono', monospace";

const labelStyle: React.CSSProperties = {
  fontFamily: monoFont,
  fontSize: 12,
  fontWeight: 300,
  textTransform: 'uppercase',
  color: 'rgba(247,246,242,0.5)',
  letterSpacing: '0.08em',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  fontFamily: monoFont,
  fontSize: 14,
  fontWeight: 300,
  padding: '10px 12px',
  backgroundColor: 'rgba(247,246,242,0.04)',
  border: '0.5px solid rgba(247,246,242,0.1)',
  color: 'rgba(247,246,242,0.85)',
  width: '100%',
  outline: 'none',
  transition: 'border-color 0.15s ease',
};

const inputFocusHandler = {
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'rgba(247,246,242,0.3)';
  },
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'rgba(247,246,242,0.1)';
  },
};

export function AdminProfile() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [savingData, setSavingData] = useState(false);
  const [dataSaved, setDataSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const n = user.user_metadata?.full_name || user.user_metadata?.name || '';
        const e = user.email || '';
        setName(n);
        setEmail(e);
        setOriginalName(n);
        setOriginalEmail(e);
      }
    };
    load();
  }, []);

  const hasDataChanges = name !== originalName || email !== originalEmail;
  const passwordValid = allCriteriaMet(newPassword);
  const canChangePassword = currentPassword.length > 0 && passwordValid && newPassword === confirmPassword && !changingPassword;

  const handleSaveData = async () => {
    setSavingData(true);
    setDataSaved(false);
    const updates: any = { data: { full_name: name } };
    if (email !== originalEmail) updates.email = email;
    const { error } = await supabase.auth.updateUser(updates);
    if (!error) {
      setOriginalName(name);
      setOriginalEmail(email);
      setDataSaved(true);
      setTimeout(() => setDataSaved(false), 2000);
    }
    setSavingData(false);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!passwordValid) {
      setPasswordError('A nova senha não atende todos os requisitos');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }
    setChangingPassword(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: originalEmail,
      password: currentPassword,
    });
    if (signInError) {
      setPasswordError('Senha atual incorreta');
      setChangingPassword(false);
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setPasswordError(error.message);
    } else {
      setPasswordChanged(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordChanged(false), 2000);
    }
    setChangingPassword(false);
  };

  const PasswordInput = ({
    value, onChange, show, onToggle, placeholder,
  }: {
    value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void; placeholder: string;
  }) => (
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => { onChange(e.target.value); setPasswordError(''); }}
        placeholder={placeholder}
        style={{ ...inputStyle, paddingRight: 40 }}
        {...inputFocusHandler}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(247,246,242,0.4)', padding: 0,
        }}
      >
        {show ? <EyeSlash size={16} weight="thin" /> : <Eye size={16} weight="thin" />}
      </button>
    </div>
  );

  return (
    <div style={{ padding: 32, color: 'rgba(247,246,242,0.85)', maxWidth: 560 }}>
      <h2 style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', opacity: 0.4, letterSpacing: '0.1em', marginBottom: 32 }}>
        PERFIL
      </h2>

      {/* Dados pessoais */}
      <div>
        <h3 style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: 'rgba(247,246,242,0.45)', letterSpacing: '0.08em', marginBottom: 16 }}>
          Dados pessoais
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Nome</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" style={inputStyle} {...inputFocusHandler} />
          </div>
          <div>
            <label style={labelStyle}>E-mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" style={inputStyle} {...inputFocusHandler} />
          </div>
        </div>
        <div style={{ marginTop: 20, opacity: hasDataChanges ? 1 : 0, transition: 'opacity 0.2s ease', pointerEvents: hasDataChanges ? 'auto' : 'none' }}>
          <button onClick={handleSaveData} disabled={savingData || !hasDataChanges}
            style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '10px 20px', border: '0.5px solid rgba(247,246,242,0.85)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.85)', cursor: 'pointer', transition: 'opacity 0.15s ease', opacity: savingData ? 0.5 : 1 }}>
            {savingData ? 'Salvando...' : dataSaved ? 'Salvo ✓' : 'Salvar Dados'}
          </button>
        </div>
      </div>

      {/* Alterar senha */}
      <div style={{ marginTop: 40, borderTop: '0.5px solid rgba(247,246,242,0.08)', paddingTop: 32 }}>
        <h3 style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: 'rgba(247,246,242,0.45)', letterSpacing: '0.08em', marginBottom: 16 }}>
          Alterar senha
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Senha atual</label>
            <PasswordInput value={currentPassword} onChange={setCurrentPassword} show={showCurrent} onToggle={() => setShowCurrent(!showCurrent)} placeholder="••••••••" />
          </div>
          <div>
            <label style={labelStyle}>Nova senha</label>
            <PasswordStrength password={newPassword} theme="dark" />
            <PasswordInput value={newPassword} onChange={setNewPassword} show={showNew} onToggle={() => setShowNew(!showNew)} placeholder="Crie uma senha segura" />
          </div>
          <div>
            <label style={labelStyle}>Confirmar nova senha</label>
            <PasswordInput value={confirmPassword} onChange={setConfirmPassword} show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} placeholder="Repita a nova senha" />
          </div>
        </div>

        {passwordError && (
          <p style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D', marginTop: 8 }}>{passwordError}</p>
        )}

        <div style={{ marginTop: 20 }}>
          <button onClick={handleChangePassword} disabled={!canChangePassword}
            style={{
              fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '10px 20px',
              border: '0.5px solid rgba(247,246,242,0.85)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.85)',
              cursor: canChangePassword ? 'pointer' : 'default', transition: 'opacity 0.15s ease',
              opacity: canChangePassword ? 1 : 0.3, pointerEvents: canChangePassword ? 'auto' : 'none',
            }}>
            {changingPassword ? 'Alterando...' : passwordChanged ? 'Senha Alterada ✓' : 'Alterar Senha'}
          </button>
        </div>
      </div>
    </div>
  );
}
